import appConfig from '../config/appConfig';

function getCurrencyDecimalPlaces() {
  const decimalPlaces = parseInt(appConfig.getCurrencyDecimalPlaces(), 10);
  return Number.isNaN(decimalPlaces) ? 2 : decimalPlaces;
}

function roundCurrencyValue(value) {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return 0;
  }

  return Number(numericValue.toFixed(getCurrencyDecimalPlaces()));
}

function roundRateValue(value) {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return 0;
  }

  return Number(numericValue.toFixed(getCurrencyDecimalPlaces()));
}

function getCalculatedChargeBySuffix(charges = {}, suffix = '') {
  const normalizedSuffix = String(suffix || '').toUpperCase();
  const matchingEntry = Object.entries(charges || {}).find(([key]) => {
    const normalizedKey = String(key || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return normalizedKey === normalizedSuffix || normalizedKey.endsWith(`_${normalizedSuffix}`);
  });

  return matchingEntry ? matchingEntry[1] : undefined;
}

export function getTimeOfUseSubtotalBasis(billData = {}) {
  const explicitSubtotal = parseFloat(billData?.subtotal);
  if (!Number.isNaN(explicitSubtotal) && explicitSubtotal > 0) {
    return explicitSubtotal;
  }

  const grossCalculated = parseFloat(
    getCalculatedChargeBySuffix(billData?.calculatedCharges, 'GROSS_CALCULATED'),
  );
  if (!Number.isNaN(grossCalculated) && grossCalculated > 0) {
    return grossCalculated;
  }

  const totalCalculated = parseFloat(billData?.calculatedCharges?.totalCalculated);
  if (!Number.isNaN(totalCalculated) && totalCalculated > 0) {
    return totalCalculated;
  }

  return 0;
}

export function getTimeOfUseCalculatedUnits(billData = {}) {
  const explicitUnits = parseFloat(
    getCalculatedChargeBySuffix(billData?.calculatedCharges, 'TOTAL_UNITS')
    ?? billData?.consumptionData?.totalConsumption
    ?? billData?.consumptionData?.total_consumption
    ?? billData?.actualKwhFromBill,
  );

  if (!Number.isNaN(explicitUnits) && explicitUnits > 0) {
    return explicitUnits;
  }

  return 0;
}

export function calculateTimeOfUseBillingRate(billData = {}) {
  const subtotal = getTimeOfUseSubtotalBasis(billData);
  const units = getTimeOfUseCalculatedUnits(billData);

  if (subtotal <= 0 || units <= 0) {
    return 0;
  }

  return roundRateValue(subtotal / units);
}

export function calculateTimeOfUseTotalCalculated(billData = {}) {
  const units = getTimeOfUseCalculatedUnits(billData);
  const rate = calculateTimeOfUseBillingRate(billData);

  if (units <= 0 || rate <= 0) {
    return 0;
  }

  return roundCurrencyValue(units * rate);
}

function createInlineRate(
  id,
  costCategory,
  rate,
  effectiveFrom,
  effectiveTo,
  description = '',
  rateType = 'FIXED',
) {
  return {
    id,
    costCategory,
    rate,
    rateType,
    effectiveFrom,
    effectiveTo,
    createdBy: 'system',
    createdAt: new Date(),
    isActive: true,
    description,
  };
}

function getPercentageBaseAmount(appliesTo = [], calculatedCharges = {}, defaultBaseAmount = 0) {
  const dependencyTotal = appliesTo.reduce((sum, categoryCode) => (
    sum + (parseFloat(calculatedCharges[categoryCode]) || 0)
  ), 0);

  return dependencyTotal > 0 ? dependencyTotal : defaultBaseAmount;
}

export function calculateTimeOfUseBill(consumptionData = {}, kvaDemand = 0, tariffData = {}) {
  const unitLines = tariffData.unitLines || consumptionData.touBreakdown || {};
  const demandLines = tariffData.demandLines || consumptionData.demandBreakdown || {};
  const percentageCategories = Array.isArray(tariffData.percentageCategories)
    ? tariffData.percentageCategories
    : [];
  const useUnitCostForBilling = tariffData.useUnitCostForBilling !== false;
  const includeOilSurcharge = tariffData.includeOilSurcharge === true;
  const includeServiceCharge = tariffData.includeServiceCharge === true;
  const oilSurchargeRate = parseFloat(tariffData.oilSurchargeRate) || 0;
  const serviceCharge = parseFloat(tariffData.serviceCharge) || 0;
  const periodStart = consumptionData.billingPeriod?.startDate || new Date();
  const periodEnd = consumptionData.billingPeriod?.endDate || new Date();

  const calculatedCharges = {};
  const ratesUsed = [];
  const unitResult = Object.entries(unitLines).reduce((result, [key, line]) => {
    const units = parseFloat(line?.units) || 0;
    const rate = parseFloat(line?.rate) || 0;
    const amount = units * rate;
    const category = line?.categoryCode;
    const lineLabel = line?.label || key;

    if (!category) {
      return {
        ...result,
        totalUnits: result.totalUnits + units,
      };
    }

    return {
      totalUnitCharges: result.totalUnitCharges + amount,
      totalUnits: result.totalUnits + units,
      charges: {
        ...result.charges,
        [category]: amount,
      },
      rates: [
        ...result.rates,
        createInlineRate(
          `time-of-use-unit-${key}`,
          category,
          rate,
          periodStart,
          periodEnd,
          `${lineLabel} unit charge`,
        ),
      ],
    };
  }, {
    totalUnitCharges: 0,
    totalUnits: 0,
    charges: {},
    rates: [],
  });

  Object.assign(calculatedCharges, unitResult.charges);
  ratesUsed.push(...unitResult.rates);

  calculatedCharges.TOTAL_UNIT_CHARGES = (
    unitResult.totalUnitCharges
  );

  const demandResult = Object.entries(demandLines).reduce((result, [key, line]) => {
    const kw = parseFloat(line?.kw) || 0;
    const rate = parseFloat(line?.rate) || 0;
    const amount = kw * rate;
    const category = line?.categoryCode;
    const lineLabel = line?.label || key;

    if (!category) {
      return result;
    }

    return {
      totalDemandCharges: result.totalDemandCharges + amount,
      charges: {
        ...result.charges,
        [category]: amount,
      },
      rates: [
        ...result.rates,
        createInlineRate(
          `time-of-use-demand-${key}`,
          category,
          rate,
          periodStart,
          periodEnd,
          `${lineLabel} demand charge`,
        ),
      ],
    };
  }, {
    totalDemandCharges: 0,
    charges: {},
    rates: [],
  });

  Object.assign(calculatedCharges, demandResult.charges);
  ratesUsed.push(...demandResult.rates);

  calculatedCharges.TOTAL_DEMAND_CHARGES = (
    demandResult.totalDemandCharges
  );
  const oilSurchargeAmount = includeOilSurcharge
    ? unitResult.totalUnits * oilSurchargeRate
    : 0;
  const serviceChargeAmount = includeServiceCharge ? serviceCharge : 0;

  if (includeOilSurcharge) {
    calculatedCharges.OIL_SURCHARGE = oilSurchargeAmount;
  }

  if (includeServiceCharge) {
    calculatedCharges.SERVICE_CHARGE = serviceChargeAmount;
  }

  const grossCalculated = unitResult.totalUnitCharges
    + demandResult.totalDemandCharges
    + oilSurchargeAmount
    + serviceChargeAmount;
  const effectivePercentageCategories = useUnitCostForBilling ? [] : percentageCategories;
  const percentageTotal = effectivePercentageCategories.reduce((sum, category) => {
    const categoryCode = category.categoryCode || category.category_code;
    const rate = parseFloat(category.rate);
    if (!categoryCode || Number.isNaN(rate)) {
      return sum;
    }

    const baseAmount = getPercentageBaseAmount(
      category.percentageAppliesTo || category.percentage_applies_to || [],
      calculatedCharges,
      grossCalculated,
    );
    const amount = roundCurrencyValue((baseAmount * rate) / 100);
    calculatedCharges[categoryCode] = amount;
    ratesUsed.push(createInlineRate(
      `time-of-use-percentage-${categoryCode}`,
      categoryCode,
      rate,
      periodStart,
      periodEnd,
      'Percentage charge',
      'PERCENTAGE',
    ));

    return sum + amount;
  }, 0);
  const directCalculatedAmount = roundCurrencyValue(grossCalculated + percentageTotal);
  const unitCostForBilling = calculateTimeOfUseBillingRate({
    subtotal: grossCalculated,
    calculatedCharges: {
      GROSS_CALCULATED: grossCalculated,
      TOTAL_UNITS: unitResult.totalUnits,
    },
    consumptionData: {
      totalConsumption: unitResult.totalUnits,
    },
  });
  const billingAdjustedAmount = unitCostForBilling > 0
    ? roundCurrencyValue(unitResult.totalUnits * unitCostForBilling)
    : roundCurrencyValue(grossCalculated);

  calculatedCharges.TOTAL_UNITS = unitResult.totalUnits;
  calculatedCharges.GROSS_CALCULATED = grossCalculated;
  calculatedCharges.BILLING_UNIT_COST = unitCostForBilling;
  calculatedCharges.totalCalculated = useUnitCostForBilling
    ? billingAdjustedAmount
    : directCalculatedAmount;

  if (includeOilSurcharge) {
    ratesUsed.push(createInlineRate(
      'time-of-use-oil-surcharge',
      'OIL_SURCHARGE',
      oilSurchargeRate,
      periodStart,
      periodEnd,
      'Oil surcharge',
    ));
  }

  if (useUnitCostForBilling && unitCostForBilling > 0) {
    ratesUsed.push(createInlineRate(
      'time-of-use-billing-unit-cost',
      'BILLING_UNIT_COST',
      unitCostForBilling,
      periodStart,
      periodEnd,
      'Billing unit cost',
    ));
  }

  return {
    calculatedCharges,
    ratesUsed,
    calculationMode: 'TIME_OF_USE',
    kvaDemand,
    subtotal: grossCalculated,
  };
}
